// Where a bug was seen: what the sighting form sends and how the timeline groups. Pure, so `sightings.test.ts` can
// run it under Node. The rules themselves are the server's (crates/server/src/routes/bugs/sightings.rs).
import type { App } from "../api/types/App";
import type { BugSighting } from "../api/types/BugSighting";
import type { RegressionFlag } from "../api/types/RegressionFlag";
import { normalizeVersion, sortVersions, versionNameError, type Orderable } from "./versions.ts";

export type SightingDraft = {
  /** Blank: not chosen. A project with one app means that one. */
  app_id: string;
  /** Set when `version` is exactly a listed version's name. */
  release_id: string | null;
  version: string;
};

export const EMPTY_DRAFT: SightingDraft = { app_id: "", release_id: null, version: "" };

type Named = { id: string; name: string };

export function chosenApp<T extends Pick<App, "id">>(draft: Pick<SightingDraft, "app_id">, apps: readonly T[]): T | undefined {
  return apps.find((a) => a.id === draft.app_id) ?? (apps.length === 1 ? apps[0] : undefined);
}

/** What the version control offers: newest first, with archived ones hidden. */
export function offeredVersions<T extends Orderable & { state: string }>(list: readonly T[]): T[] {
  return sortVersions(list.filter((v) => v.state !== "archived"));
}

/** The draft for text from the version control. Only a listed name becomes a link here; the server links other text it can match. */
export function withVersionText(draft: SightingDraft, text: string, versions: readonly Named[]): SightingDraft {
  const lower = text.trim().toLowerCase();
  const picked = lower ? versions.find((v) => v.name.toLowerCase() === lower) : undefined;
  return { ...draft, release_id: picked?.id ?? null, version: picked?.name ?? text.trim() };
}

/** The version this text will link to, ignoring case, a leading `v` and `+build`. */
export function matchingVersion<T extends Pick<Named, "name">>(text: string, versions: readonly T[]): T | undefined {
  const wanted = normalizeVersion(text);
  return wanted ? versions.find((v) => normalizeVersion(v.name) === wanted) : undefined;
}

/** A request's place fields. */
export function sightingPlace(draft: SightingDraft, apps: readonly Pick<App, "id">[]) {
  const app = chosenApp(draft, apps);
  return {
    app_id: app?.id ?? null,
    release_id: draft.release_id,
    version: draft.release_id ? "" : draft.version.trim(),
  };
}

/** Several apps: a sighting has to say which. */
export function appMissing(draft: Pick<SightingDraft, "app_id">, apps: readonly Pick<App, "id">[]): boolean {
  return apps.length > 1 && !chosenApp(draft, apps);
}

export type VersionGroup = { key: string; release_id: string | null; name: string | null; sightings: BugSighting[] };
export type AppGroup = { key: string; app_id: string | null; app_name: string | null; versions: VersionGroup[] };

/**
 * The timeline: by app, then by version, each in the order first seen. Text that hasn't linked gets its own group,
 * as does "version unknown" (`name` null).
 */
export function groupSightings(sightings: readonly BugSighting[]): AppGroup[] {
  const apps = new Map<string, AppGroup>();
  const versions = new Map<string, VersionGroup>();
  for (const s of sightings) {
    const appKey = s.app_id ?? "none";
    let app = apps.get(appKey);
    if (!app) {
      app = { key: appKey, app_id: s.app_id, app_name: s.app_name, versions: [] };
      apps.set(appKey, app);
    }
    const name = s.release_name ?? (s.reported_version || null);
    const versionKey = s.release_id ?? `text:${name ? normalizeVersion(name) : ""}`;
    const fullKey = `${appKey}/${versionKey}`;
    let version = versions.get(fullKey);
    if (!version) {
      version = { key: fullKey, release_id: s.release_id, name, sightings: [] };
      versions.set(fullKey, version);
      app.versions.push(version);
    }
    version.sightings.push(s);
  }
  return [...apps.values()];
}

/**
 * Whether "make this a version" can work: unlinked text that's a valid name, on an active app whose versions can be
 * added by hand. A deployment app's come from GitLab, so one added by hand needs a date this action doesn't ask for.
 */
export function canMakeVersion(s: Pick<BugSighting, "release_id" | "reported_version">, app: Pick<App, "source" | "archived"> | undefined): boolean {
  if (s.release_id || !s.reported_version || !app || app.archived || app.source === "deployment") return false;
  return versionNameError(normalizeVersion(s.reported_version)) === null;
}

export function openFlags(flags: readonly RegressionFlag[]): RegressionFlag[] {
  return flags.filter((f) => f.state === "open");
}

/** A flag's sighted version, as the timeline names it. */
export function flagVersion(flag: Pick<RegressionFlag, "sighting_id">, sightings: readonly BugSighting[]): string {
  const s = sightings.find((x) => x.id === flag.sighting_id);
  return s?.release_name ?? s?.reported_version ?? "";
}

/** An app's name with the version it was first seen on, if any. */
export function appWithFoundIn(app: { id: string; name: string }, foundIn: readonly { app_id: string; release_name: string }[]): string {
  const found = foundIn.find((f) => f.app_id === app.id);
  return found ? `${app.name} ${found.release_name}` : app.name;
}
